import { useEffect, useState } from 'react';
import { ApiError, apiGet } from './api';

export type Loadable<T> =
  | { status: 'loading' }
  | { status: 'error'; error: ApiError | Error }
  | { status: 'ready'; data: T };

const TIMEOUT_MS = 15_000;

/**
 * Loads `path` from the API and reloads when it changes, or when `reloadKey` does (bump it
 * after a change). `null` skips the request.
 */
export function useApi<T>(path: string | null, reloadKey = 0): Loadable<T> {
  const [state, setState] = useState<Loadable<T>>({ status: 'loading' });

  useEffect(() => {
    if (path === null) return;
    // `active` turns false when the page moves on, so a late answer can't overwrite a newer one.
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
    setState((current) =>
      // Reloading the same data keeps it on screen instead of flashing "Loading…".
      current.status === 'ready' && reloadKey > 0 ? current : { status: 'loading' },
    );

    apiGet<T>(path, controller.signal)
      .then((data) => {
        if (active) setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const failure =
          error instanceof ApiError
            ? error
            : new Error(
                controller.signal.aborted
                  ? 'The API took too long to answer. Try again.'
                  : 'Could not reach the API. Check your connection and try again.',
              );
        setState({ status: 'error', error: failure });
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [path, reloadKey]);

  return state;
}
